import React from 'react';
import { DollarSign, Plane, Hotel, FileText } from 'lucide-react';

/**
 * Компонент секции расходов командировки
 */
const ExpensesSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-indigo-500" />
        Расходы
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Транспортные расходы */}
        <div className="col-span-full">
          <div className="flex items-center mb-2">
            <label htmlFor="transportationCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Plane className="w-4 h-4 mr-1 text-indigo-500" />
              Транспортные расходы
            </label>
            <label className="inline-flex items-center ml-4">
              <input
                type="checkbox"
                name="includeTransportation"
                checked={formData.includeTransportation}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Включить транспорт
              </span>
            </label>
          </div>
          
          {formData.includeTransportation && (
            <div className="flex">
              <input
                type="text"
                id="transportationCost"
                name="transportationCost"
                value={formData.transportationCost}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.transportationCost ? 'input-error' : ''}`}
                placeholder="Введите стоимость транспорта"
              />
              <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm rounded-r-md">
                ₽
              </span>
            </div>
          )}
          
          {formData.includeTransportation && errors.transportationCost && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.transportationCost}
            </p>
          )}
          
          {formData.includeTransportation && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Общая стоимость проезда туда и обратно, включая все сборы.
            </p>
          )}
        </div>

        {/* Расходы на проживание */}
        <div className="col-span-full">
          <div className="flex items-center mb-2">
            <label htmlFor="accommodationCostPerDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Hotel className="w-4 h-4 mr-1 text-indigo-500" />
              Расходы на проживание
            </label>
            <label className="inline-flex items-center ml-4">
              <input
                type="checkbox"
                name="includeAccommodation"
                checked={formData.includeAccommodation}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Включить проживание
              </span>
            </label>
          </div>
          
          {formData.includeAccommodation && (
            <div className="flex">
              <input
                type="text"
                id="accommodationCostPerDay"
                name="accommodationCostPerDay"
                value={formData.accommodationCostPerDay}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.accommodationCostPerDay ? 'input-error' : ''}`}
                placeholder="Введите стоимость проживания"
              />
              <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm rounded-r-md">
                ₽/сутки
              </span>
            </div>
          )}
          
          {formData.includeAccommodation && errors.accommodationCostPerDay && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.accommodationCostPerDay}
            </p>
          )}
          
          {formData.includeAccommodation && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Стоимость проживания за одни сутки. Будет учтено количество ночей в соответствии с датами.
            </p>
          )}
        </div>

        {/* Дополнительные расходы */}
        <div className="col-span-full">
          <div className="flex items-center mb-2">
            <label htmlFor="additionalExpensesAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <FileText className="w-4 h-4 mr-1 text-indigo-500" />
              Дополнительные расходы
            </label>
            <label className="inline-flex items-center ml-4">
              <input
                type="checkbox"
                name="includeAdditionalExpenses"
                checked={formData.includeAdditionalExpenses}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Включить доп. расходы
              </span>
            </label>
          </div>
          
          {formData.includeAdditionalExpenses && (
            <>
              <div className="flex mb-2">
                <input
                  type="text"
                  id="additionalExpensesAmount"
                  name="additionalExpensesAmount"
                  value={formData.additionalExpensesAmount}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.additionalExpensesAmount ? 'input-error' : ''}`}
                  placeholder="Введите сумму доп. расходов"
                />
                <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm rounded-r-md">
                  ₽
                </span>
              </div>
              
              <input
                type="text"
                id="additionalExpensesDescription"
                name="additionalExpensesDescription"
                value={formData.additionalExpensesDescription}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.additionalExpensesDescription ? 'input-error' : ''}`}
                placeholder="Описание дополнительных расходов"
              />
            </>
          )}
          
          {formData.includeAdditionalExpenses && errors.additionalExpensesAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.additionalExpensesAmount}
            </p>
          )}
          
          {formData.includeAdditionalExpenses && errors.additionalExpensesDescription && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.additionalExpensesDescription}
            </p>
          )}
          
          {formData.includeAdditionalExpenses && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Укажите дополнительные расходы (например, бензин, парковка, интернет, представительские расходы).
            </p>
          )}
        </div>
      </div>

      {/* Информационный блок */}
      <div className="mt-4 bg-indigo-100 dark:bg-indigo-900/30 p-3 rounded-lg border border-indigo-200 dark:border-indigo-700">
        <p className="text-xs text-indigo-700 dark:text-indigo-300 flex items-start">
          <DollarSign className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
          <span>
            По законодательству РФ компенсации за подтвержденные расходы на проезд и проживание не облагаются НДФЛ.
            Суточные не облагаются НДФЛ в пределах нормативов (700 руб. для внутрироссийских и 2500 руб. для зарубежных командировок).
            Сумма превышения нормативов подлежит налогообложению.
          </span>
        </p>
      </div>
    </div>
  );
};

export default ExpensesSection;